import { makeStyles } from '@material-ui/core';
import { Close } from '@material-ui/icons';
import clsx from 'clsx';
import React from 'react';
import { AlertCircle } from 'react-feather';
import { TFunction, useTranslation } from 'react-i18next';
import { actions, useTypedDispatch, useTypedSelector } from '../../../../redux';
import { theme } from '../../../../theme/theme';
import { Asset } from '../../../../types/asset';
import { enumToStatusType, probabilities } from '../../../../types/common';
import {
    computeOverallRisk,
    consequenceIndex,
    likelihoodIndex,
    overallRiskMatrix,
    Plan,
    RiskAssessment,
} from '../../../../types/plan';
import { Badge, Property, StatusIcon } from '../../../global';
import { SectionState } from '../InspectionCreationWizard';

const mod = 'sections.RiskAssessment';

/**
 * Number of internal steps this section contains.
 */
export const steps = 1;
export const label = `${mod}.summary-label`;

/**
 * Header generator.
 *
 * @param t translation function
 * @param state the current section state
 * @param focused if is the current section
 * @param asset selected asset
 * @param plan asset plan
 * @returns header title and subtitle
 */
export const header = (t: TFunction<string[]>, state: SectionState, focused: boolean, asset?: Asset, plan?: Plan) => ({
    title: t(`${mod}.header-title${!focused ? '-small' : ''}`),
    subtitle: t(`${mod}.header-subtitle${!focused ? '-small' : ''}`, { count: 1, document: 'API653' }),
});

const useComponentClasses = makeStyles({
    root: {
        display: 'flex',
        flex: 1,
        margin: '0 -10px',
    },
    column: {
        display: 'flex',
        flexDirection: 'column',
        margin: '0 10px',
        '&:first-child': {
            flex: 1,
            margin: '-10px 10px',
            '& > *': {
                margin: '10px 0',
            },
        },
    },
});

/**
 * Risk assessment information of the inspection plan creation wizard.
 *
 * @param props.state the current section state
 * @param props.next function to advance to the next step
 */
export const Component = (props: { state: SectionState; next: () => void }) => {
    const classes = useComponentClasses();
    const { t } = useTranslation(['inspection-creation']);
    const dispatch = useTypedDispatch();
    const { plan } = useTypedSelector(state => ({ plan: state.plan.changed }));

    if (plan && !plan.risk_assessment)
        plan.risk_assessment = {
            id: -1,
            likelihood_risk: 'low',
            likelihood_description: '',
            consequence_risk: 'low',
            consequence_description: '',
        };

    const updateRiskAssessment = (changes: Partial<RiskAssessment>) => {
        dispatch(actions.plan.edit({ risk_assessment: { ...plan?.risk_assessment!, ...changes } }));
    };

    if (!plan) return <span>{'#plan not in store'}</span>;

    return (
        <div className={classes.root}>
            <div className={classes.column}>
                <span>{t(`${mod}.likelihood-title`)}</span>
                <Property
                    editable={props.state.editable}
                    name={t(`${mod}.probability`)}
                    value={plan.risk_assessment!.likelihood_risk}
                    options={probabilities}
                    renderOption={option => (
                        <Badge style={{ display: 'inline-flex' }} type={enumToStatusType[option]}>
                            {option}
                        </Badge>
                    )}
                    onChange={(_, value) =>
                        value != undefined && updateRiskAssessment({ likelihood_risk: value as any })
                    }
                />
                <Property
                    editable={props.state.editable}
                    name={t(`${mod}.justification`)}
                    multiline
                    value={plan.risk_assessment!.likelihood_description}
                    onChange={(_, value) => updateRiskAssessment({ likelihood_description: value })}
                />
                <span>{t(`${mod}.consequence-title`)}</span>
                <Property
                    editable={props.state.editable}
                    name={t(`${mod}.probability`)}
                    value={plan.risk_assessment!.consequence_risk}
                    options={probabilities}
                    renderOption={option => (
                        <Badge style={{ display: 'inline-flex' }} type={enumToStatusType[option]}>
                            {option}
                        </Badge>
                    )}
                    onChange={(_, value) =>
                        value != undefined && updateRiskAssessment({ consequence_risk: value as any })
                    }
                />
                <Property
                    editable={props.state.editable}
                    name={t(`${mod}.justification`)}
                    multiline
                    value={plan.risk_assessment!.consequence_description}
                    onChange={(_, value) => updateRiskAssessment({ consequence_description: value })}
                />
            </div>
            <div className={classes.column}>
                <RiskMatrix riskAssessment={plan.risk_assessment!} />
            </div>
        </div>
    );
};

const useRiskMatrixClasses = makeStyles({
    root: {
        display: 'grid',
        gridTemplateRows: '50px repeat(5, 45px) 30px',
        gridTemplateColumns: '30px repeat(5, 45px)',
        gap: 5,
        padding: '0 10px',
        borderRadius: 5,
        background: theme.colors.neutral[4],
        boxShadow: '0 1px 1px 0 rgba(0, 0, 0, 0.14)',
    },
    header: {
        display: 'flex',
        alignItems: 'center',
    },
    statusIcon: {
        margin: '0 10px 0 0',
    },
    heading: {
        display: 'flex',
        flexDirection: 'column',
        '& > span:first-child': {
            color: theme.colors.neutral[1],
            ...theme.typography.content.caption.paragraph,
        },
        '& > span:last-child': {
            color: theme.colors.neutral[1],
            ...theme.typography.headings.h6,
            marginBottom: 0,
            textTransform: 'uppercase',
            '&.moderate': {
                color: '#FEDF9A',
            },
            '&.moderate-high': {
                color: '#FF8A65',
            },
            '&.high': {
                color: '#BE4242',
            },
        },
    },
    axis: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        color: theme.colors.neutral[2],
        ...theme.typography.headings.h6,
        marginBottom: 0,
        textTransform: 'uppercase',
    },
    tick: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        color: theme.colors.neutral[3],
        ...theme.typography.content.caption.paragraph,
        textTransform: 'uppercase',
    },
    cell: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        color: theme.colors.neutral[1],
        borderRadius: 2,
        '&.selected': {
            border: '1px solid currentColor',
        },
        '&.low': {
            background: theme.colors.white,
        },
        '&.moderate': {
            background: '#FEDF9A',
        },
        '&.moderate-high': {
            background: '#FF8A65',
        },
        '&.high': {
            background: '#BE4242',
        },
    },
});

const RiskMatrix = (props: { riskAssessment: RiskAssessment }) => {
    const classes = useRiskMatrixClasses();
    const { t } = useTranslation(['inspection-creation']);
    const row = likelihoodIndex[props.riskAssessment.likelihood_risk];
    const col = consequenceIndex[props.riskAssessment.consequence_risk];
    const risk = computeOverallRisk(props.riskAssessment);

    return (
        <div className={classes.root}>
            <div className={classes.header} style={{ gridRow: 1, gridColumn: '1 / 6' }}>
                <StatusIcon className={classes.statusIcon} type='warning' icon={<AlertCircle />} />
                <div className={classes.heading}>
                    <span>{t(`${mod}.risk`)}</span>
                    <span className={risk}>{risk}</span>
                </div>
            </div>
            <div className={classes.tick} style={{ gridRow: 2, gridColumn: 1 }}>
                {t(`${mod}.high`)}
            </div>
            <div
                className={classes.axis}
                style={{ gridRow: '3 / 6', gridColumn: 1, writingMode: 'vertical-lr', transform: 'rotate(180deg)' }}
            >
                {t(`${mod}.likelihood`)}
            </div>
            <div className={classes.tick} style={{ gridRow: 6, gridColumn: 1 }}>
                {t(`${mod}.low`)}
            </div>
            <div className={classes.tick} style={{ gridRow: 7, gridColumn: 2 }}>
                {t(`${mod}.low`)}
            </div>
            <div className={classes.axis} style={{ gridRow: 7, gridColumn: '3 / 6' }}>
                {t(`${mod}.consequence`)}
            </div>
            <div className={classes.tick} style={{ gridRow: 7, gridColumn: 6 }}>
                {t(`${mod}.high`)}
            </div>
            {[...Array(5)].flatMap((_, i) => [
                [...Array(5)].map((_, j) => (
                    <div
                        key={i * 5 + j}
                        className={clsx(classes.cell, overallRiskMatrix[i][j], row === i && col === j && 'selected')}
                        style={{ gridRow: i + 2, gridColumn: j + 2 }}
                    >
                        {row === i && col === j && <Close />}
                    </div>
                )),
            ])}
        </div>
    );
};
